/*!
 * liveSignals.js — what the beat and every sound put on the signal bus
 * © 2026 Rob Canning — GPLv3
 *
 * About 30 times a second, for follow():
 *
 *   beat.phase   0..1 through the beat (the tempo() grid, shared by clients)
 *   beat.n       the beat's number          beat.bar   the bar's (4 beats)
 *                (counted from the shared clock's start: large; for "every
 *                4th" and the like, not for "the 3rd beat of the piece")
 *   beat.bpm     the tempo
 *   beat.hit     1 for a moment on each beat; beat.hit4 on every 4th (each
 *                bar's first), beat.hit8, beat.hit16 ... -- any N
 *   beat.div2    1 for a moment on each half beat (8ths); div4 16ths, div3
 *                triplets, div8 ... -- any N
 *                (on the frame: a strobe lands when the beat does; made only
 *                while followed)
 *
 *   <name>.level how loud a sound is, 0..1 of its own loudest lately -- so
 *                follow(drums.level, lo, hi) reaches hi whatever its amp
 *   <name>.rms   the same, unscaled
 *   <name>.fader its fader (level(): 1 as written, 0 silent) -- both of the
 *                above are read after it, so a fade shows in them too
 *   <name>.env   a synth's envelope, 0..1: where its notes' a d s r have got
 *                to (the loudest voice) -- read from the envelope itself, so
 *                a fuzz or a compressor flattening the sound does not flatten it
 *   <name>.low / .mid / .high   its energy's share below 250 Hz, to 2 kHz,
 *                above (adding up to 1, whatever its level); .centroid its
 *                brightness 0..1 (spectrumBands.js) -- made only while followed
 *   <name>.hit   1 for ~0.1 s on each event: a synth's step, a file starting
 *                (each audioPool / audioImpulse grain)
 *   <name>.hitN  the same on every Nth event only: hit4 on the 1st, 5th, 9th
 *                ... -- any N, made only while something follows it
 *   <name>.n     how many events so far
 *
 *   mouse.x, mouse.y          the pointer, 0..1 across and down the window (a
 *                             touch too); the middle until it moves
 *   mouse.down                1 while a button (a finger) is down
 *   mouse.hit, mouse.n        a press -- trig:mouse.hit, every(4, mouse.hit)
 *   view.zoom                 the view's zoom
 *   (transport.t / elapsed / speed / playing: RAF.js, every frame)
 *   page.hit, page.n          a page turned (shown, changed, closed)
 *   <button id>.hit / .n      a button pressed (button.js)
 *
 * Written every(4, bass.hit) and beat.div(4) (parser.js countsToChannels),
 * they arrive here as the channels hit4 and div4, made while followed.
 *
 * A sound is named as it was: synth(uid:bass), audio(uid:drums); its poly
 * and pool voices (bass__2, drums__17) count as that name. Levels rise at
 * once and fall over about 0.2 s.
 */

import { publish } from "./paramBinding.js";
import { subscribedPaths } from "./paramBus.js";
import { emptyBands, addSpectrum, bandShares, bandsWanted, addBandPowers, bandLevels } from "./spectrumBands.js";
import { beatAt, gridNow, gridBpm } from "../cues/audio/beatGrid.js";
import { levelOf } from "../cues/audio/level.js";

const RATE_MS = 33;
const RELEASE = 0.85;          // per tick: about 0.2 s to fall away
const PEAK_FALL = 0.997;       // the "loudest lately" forgets over ~10 s

const sounds = new Map();      // name -> { level, peak, keys:Set }: the loudness
const events = new Map();      // name -> { n, hit, type }: counts kept for the session
let buf = null;

function rmsOf(analyser) {
  const n = analyser.fftSize;
  if (!buf || buf.length !== n) buf = new Float32Array(n);
  analyser.getFloatTimeDomainData(buf);
  let s = 0;
  for (let i = 0; i < n; i += 4) s += buf[i] * buf[i];
  return Math.sqrt(s / (n / 4));
}

const nameOf = key => String(key ?? "").split("__")[0];

function soundState(name) {
  let st = sounds.get(name);
  if (!st) sounds.set(name, st = { level: 0, peak: 0, keys: new Set() });
  return st;
}

const SAME_HIT_MS = 30;          // events of one sound this close together are one
const HIT_TICKS = 3;            // a hit stays 1 for about 0.1 s: long enough to see

/**
 * An event: <name>.hit is 1 for about 0.1 s, then 0; <name>.n counts.
 * `extra`: channels to send with it, in the same message (a synth's step).
 */
export function pulse(name, sourceType = "sound", extra = null) {
  if (!name) return;
  let ev = events.get(name);
  if (!ev) events.set(name, ev = { n: 0, hit: 0, type: sourceType });
  // a poly sound's voices stepping together (a chord, four voices on one
  // beat) are one hit, not four: n counts steps, and hit4 is every 4th step
  // -- four at once also left only the last on the bus (the throttle), so
  // hit4 seldom got through
  const t = performance.now();
  if (ev.at != null && t - ev.at < SAME_HIT_MS) return;
  ev.at = t;
  ev.n += 1;
  // still up from the last one (words of a text a sixteenth apart): down for
  // a moment first, so a trig: sees this one rise too
  if (ev.hit > 0) publish(sourceType, name, { hit: 0 });
  ev.hit = HIT_TICKS;
  ev.type = sourceType;
  // hit4, hit3 ...: 1 on every Nth event -- only the ones followed
  ev.every = {};
  for (const path of subscribedPaths(`${name}.hit`)) {
    const k = Number(/\.hit(\d+)$/.exec(path)?.[1]);
    if (k > 0) ev.every[`hit${k}`] = (ev.n - 1) % k === 0 ? 1 : 0;
  }
  publish(sourceType, name, { ...(extra || {}), hit: 1, n: ev.n, ...ev.every });
}

// The view's zoom, from the page that has one (the score's scoreZoom.js)
let getZoom = null;
export function setZoomSource(fn) { getZoom = fn; }
let lastPage = null;

function tick() {
  // the view (the transport is published by RAF.js, every frame)
  const z = getZoom?.();
  if (Number.isFinite(z)) publish("view", "view", { zoom: z });
  // a page turned: the page overlay showing another page, or none
  const pc = document.getElementById("singlePage-container");
  const page = pc && !pc.hidden && pc.style.display !== "none"
    ? (new URLSearchParams(location.search).get("page") || "open") : "";
  if (lastPage !== null && page !== lastPage) pulse("page", "page");
  lastPage = page;

  // the beat
  const ms = gridNow().ms;
  const b = beatAt(ms);
  if (Number.isFinite(b)) {
    publish("beat", "beat", { phase: b - Math.floor(b), n: Math.floor(b), bar: Math.floor(b / 4), bpm: gridBpm(ms) });
  }

  // every sound: the loudest of its voices
  const loud = new Map(), keys = new Map(), envs = new Map(), spectra = new Map();
  const bandSums = new Map();      // name -> { n: power sums } for band(k, n)
  // low / mid / high / centroid, band(k, n): only for a sound something follows them of
  const wanted = new Map();
  const wantsOf = name => {
    if (!wanted.has(name)) {
      const paths = subscribedPaths(`${name}.`);
      wanted.set(name, { shares: paths.some(p => /\.(low|mid|high|centroid)$/.test(p)), bands: bandsWanted(paths, `${name}.`) });
    }
    return wanted.get(name);
  };
  const addBands = (name, analyser) => {
    if (!analyser) return;
    const w = wantsOf(name), ns = Object.keys(w.bands);
    if (!w.shares && !ns.length) return;
    const buf = analyser._bandsBuf?.length === analyser.frequencyBinCount ? analyser._bandsBuf
      : (analyser._bandsBuf = new Float32Array(analyser.frequencyBinCount));
    analyser.getFloatFrequencyData(buf);
    const sr = analyser.context.sampleRate;
    if (w.shares) addSpectrum(spectra.get(name) || spectra.set(name, emptyBands()).get(name), buf, sr);
    const sums = bandSums.get(name) || bandSums.set(name, {}).get(name);
    for (const n of ns) addBandPowers(sums[n] ||= new Float64Array(Number(n)), buf, sr, Number(n));
  };
  const take = (key, analyser) => {
    if (!analyser) return;
    const name = nameOf(key);
    if (!name) return;
    try { loud.set(name, Math.max(loud.get(name) || 0, rmsOf(analyser))); } catch { return; }
    (keys.get(name) || keys.set(name, new Set()).get(name)).add(String(key));
  };
  for (const [k, v] of window.activeSynthVoices || []) {
    take(k, v?.graph?.analyser);
    addBands(nameOf(k), v?.graph?.analyser);
    // the envelope: the gain it draws, over the amp it peaks at
    const g = v?.graph?.gain?.gain?.value, amp = v?.amp;
    const name = nameOf(k);
    if (name && Number.isFinite(g) && amp > 0) envs.set(name, Math.max(envs.get(name) || 0, Math.min(1, g / amp)));
  }
  for (const [k, v] of window.activeAudioCues || []) { take(k, v?.analyser); addBands(nameOf(k), v?.analyser); }

  for (const name of new Set([...loud.keys(), ...sounds.keys()])) {
    const st = soundState(name);
    // a voice that was not there last tick: a file starting, a grain
    const now = keys.get(name) || new Set();
    for (const k of now) if (!st.keys.has(k) && !window.activeSynthVoices?.has?.(k)) pulse(name, "sound");
    st.keys = now;

    // the analysers read before the fader (level.js): after it is what is heard
    const fader = levelOf(name);
    const rms = (loud.get(name) || 0) * fader;
    st.level = Math.max(rms, st.level * RELEASE);
    st.peak = Math.max(st.level, st.peak * PEAK_FALL, 1e-4);
    // where its energy is: smoothed a little, it moves with the notes, not the frames
    const sh = spectra.has(name) ? bandShares(spectra.get(name)) : null;
    if (sh) {
      const b = st.bands || (st.bands = { ...sh });
      for (const key in sh) b[key] += 0.5 * (sh[key] - b[key]);
    }
    // band(k, n): each against its own loudest lately (spectrumBands.js)
    let bands = null;
    for (const [n, sum] of Object.entries(bandSums.get(name) || {})) {
      st.bandState ||= {};
      const heard = sum.map(x => x * fader * fader);     // after the fader, as level is: a faded sound's bands fall
      bands = { ...bands, ...bandLevels(heard, Number(n), wantsOf(name).bands[n], st.bandState[n] ||= {}) };
    }
    publish("sound", name, { level: Math.min(1, st.level / st.peak), rms, fader,
      ...(envs.has(name) ? { env: envs.get(name) } : {}), ...(sh ? st.bands : {}), ...bands });

    // silent and gone: say 0 once, then forget it
    if (!now.size && st.level < 1e-4) {
      publish("sound", name, { level: 0, rms: 0, env: 0 });
      sounds.delete(name);
    }
  }

  // a hit stays 1 for a few ticks, then 0
  for (const [name, ev] of events) {
    if (ev.hit > 0 && --ev.hit === 0) {
      const zeros = {};
      for (const k in ev.every || {}) zeros[k] = 0;
      publish(ev.type, name, { hit: 0, ...zeros });
    }
  }
}

// ---- the clock's hits: beat.hit, beat.hit4, beat.div4 ... -------------
// Each frame (not the 30-a-second tick above: a strobe should land on the
// beat), only while something follows one. Each is 1 from the moment its
// beat (or part of one) comes, for ~70 ms or half its length, then 0.
const clockHits = new Map();       // channel -> { last: index, off: ms }
function clockFrame() {
  requestAnimationFrame(clockFrame);
  clockStep();
}
// A hidden page has no frames: the worker clock (backgroundClock.js, in
// both apps) steps the hits instead (below).
function clockStep() {
  const want = subscribedPaths("beat.").filter(p => /^beat\.(hit\d*|div\d+(e\d+)?)$/.test(p));
  if (!want.length && !clockHits.size) return;
  const ms = gridNow().ms, now = performance.now();
  const b = beatAt(ms);
  if (!Number.isFinite(b)) return;
  const secsPerBeat = 60 / (gridBpm(ms) || 60);
  const out = {};
  for (const path of want) {
    const ch = path.slice(5);
    const [, kind, nRaw, eRaw] = /^(hit|div)(\d*)(?:e(\d+))?$/.exec(ch);
    const n = Math.max(1, Number(nRaw) || 1);
    // div: n parts to a beat; hit: every n-th beat (counted on the shared grid: hit4 on the bar);
    // div4e3: every 3rd of the 4 parts (every(3, beat.div(4)): dotted 8ths), on the grid too
    const every = Math.max(1, Number(eRaw) || 1);
    const index = kind === "div" ? Math.floor(b * n) : Math.floor(b);
    const lastsMs = Math.min(70, 500 * secsPerBeat * (kind === "div" ? every / n : n));
    let st = clockHits.get(ch);
    if (!st) { clockHits.set(ch, st = { last: index, off: 0 }); continue; }   // from the next one
    if (index !== st.last) {
      st.last = index;
      if (kind === "div" ? index % every === 0 : index % n === 0) { out[ch] = 1; st.off = now + lastsMs; }
    } else if (st.off && now >= st.off) { out[ch] = 0; st.off = 0; }
  }
  for (const ch of [...clockHits.keys()]) if (!want.includes(`beat.${ch}`)) clockHits.delete(ch);   // no longer followed
  // under its own source: the beat's other channels (tick) are throttled apart
  if (Object.keys(out).length) publish("clock", "beat", out);
}

// The pointer, wherever it is in the window -- over the code in perform mode
// too (the events pass through it to the page): published as it moves,
// the publish throttle keeping it to one message a frame
function pointerSignals() {
  const at = e => ({ x: Math.min(1, Math.max(0, e.clientX / (innerWidth || 1))), y: Math.min(1, Math.max(0, e.clientY / (innerHeight || 1))) });
  publish("mouse", "mouse", { x: 0.5, y: 0.5, down: 0 });
  addEventListener("pointermove", e => publish("mouse", "mouse", at(e)), { passive: true, capture: true });
  addEventListener("pointerdown", e => { pulse("mouse", "mouse", { ...at(e), down: 1 }); }, { passive: true, capture: true });
  addEventListener("pointerup", e => publish("mouse", "mouse", { ...at(e), down: 0 }), { passive: true, capture: true });
}

if (typeof window !== "undefined" && !window.__oscillaLiveSignals) {
  window.__oscillaLiveSignals = setInterval(tick, RATE_MS);
  requestAnimationFrame(clockFrame);
  // hidden, the timers are clamped to once a second: the worker clock keeps
  // the tick at its rate and steps the hits each 16 ms
  let lastBgTick = 0;
  const background = (now) => {
    clockStep();
    if (now - lastBgTick >= RATE_MS) { lastBgTick = now; tick(); }
  };
  window.oscillaBackgroundClock?.onBackgroundTick(background);
  window.addEventListener("oscilla:background-clock-ready", () => window.oscillaBackgroundClock?.onBackgroundTick(background), { once: true });
  pointerSignals();
}
